import { updatePastedBatchDisplay, copyToClipboard } from 'lib/util.js';
import { uuid } from '../../../shared/lib/uuid';
import { Node, Link } from '../store';
import { pui } from 'lib/perspectives-ui';
import { pcui } from 'lib/perspective-creation-ui';
import { nui } from 'lib/nodes-ui';
import { regionUI } from 'lib/region-ui';
import { appUI } from 'lib/app-ui';
import { ALLOWED_IMAGE_FILE_TYPES } from 'lib/constants';
const STORAGE_KEY = 'clipboard-storage';

let pasteOffsetX = 50;
let pasteOffsetY = 50;


function _resetPasteOffset() {
  pasteOffsetX = 50;
  pasteOffsetY = 50;
}

function _incrementPasteOffset() {
  pasteOffsetX += 20;
  pasteOffsetY += 20;
}

function pasteContentExists() {
  const _json = localStorage.getItem('clipboard-storage');
  const data = JSON.parse(_json);
  if (!data) {
    return false;
  }
  return true;
}

function cloneSelectionInPlace({canvas}) {
  const data = copyData({canvas});
  return pasteData({canvas, data, pasteWithinEnabled: false, inPlace: true});
}

function copy({canvas,event}) {
  const {nodes,links,perspectives,regions,mapId} = copyData({canvas});
  const json = JSON.stringify({nodes,links,perspectives,regions,mapId});

  copyToClipboard({
    event,
    text: getSelectionAsPlainText({canvas}),
    html: getSelectionAsHTML({
      canvas,
      encodedPayload: btoa(encodeURIComponent(json)),
    }),
    plecticaJSON: json
  });

  localStorage.setItem(STORAGE_KEY, json);
  _resetPasteOffset();
}
function copyData({canvas}) {

  // grab all nodes and links in selection and get objects we can serialize to json
  const nodes = [];
  const links = [];
  const seenNode = {};
  const seenLinks = {};
  canvas.selectedElements.forEach(element => {

    const instance = element.instance;
    if (instance instanceof Node) {

      if (!seenNode[instance.id]) {
        nodes.push(instance.dump());
        seenNode[instance.id] = 1;
      }

      // if node has children, include all of them recursively
      const descendants = instance.descendants();
      for (let node of descendants) {
        if (!seenNode[node.id]) {
          const nodeDump = node.dump();
          nodeDump._governingLayoutName = node.getGoverningLayoutName();
          nodes.push(nodeDump);
          seenNode[node.id] = 1;
        }
      }

      // if we selected a freehand container, grab all the links in it too
//      if (instance.getLayoutName() == 'layout-freehand') {
      const nodeLinks = instance.familyLinks();
      nodeLinks.forEach(l => {
        if (!seenLinks[l.id]) {
          seenLinks[l.id] = 1;
          links.push(l.dump());


          if (l.linkNodeId && !seenNode[l.linkNodeId]) {
            nodes.push(l.linkNode().dump());
            seenNode[l.linkNodeId] = 1;

              // copy children of R-nodes
            const descendants = l.linkNode().descendants();
            for (let node of descendants) {
              if (!seenNode[node.id]) {
                const nodeDump = node.dump();
                nodeDump._governingLayoutName = node.getGoverningLayoutName();
                nodes.push(nodeDump);
                seenNode[node.id] = 1;
              }
            }
          }
        }
      });
//      }
      if (instance.parentId) {
        const pos = canvas.nodeTags[instance.id].discoverPosition(true); //discover position without setting it in the store;
        if (pos) {
          const n = nodes.find(n => n.id == instance.id);
          n.absolutePosition = pos;
        }
      }

    } else if (instance instanceof Link) {
      if (!seenLinks[instance.id]) {
        links.push(instance.dump());
        seenLinks[instance.id] = 1;
      }
      // also copy the link node if we have one
      const linkNode = instance.linkNode();
      if (linkNode) {
        if (!seenNode[linkNode.id]) {
          nodes.push(linkNode.dump());
          seenNode[linkNode.id] = 1;
        }
      }
    }
  });

  const regions = regionUI.getSelectedRegions();
  const perspectives = canvas.store.getPerspectives();
  const mapId = canvas.store.getMapId();
  return {nodes,links,perspectives,regions,mapId};
}

function paste({ canvas, pasteWithinEnabled, position, event }) {
  let importJSON;
  let parentId;
  if (pasteWithinEnabled && canvas.getSelectedNodes().length == 1) {
    parentId = canvas.getSelectedNodes()[0].id;
  }

  if (event && event.clipboardData) {

    const isImage = clipboardHasImage(event);

    if (isImage) {
      /* create a card and paste the image in */
      return createCardAndAddAttachment({ canvas, event, position, parentId });

    }
    const clipboardPlecticaJSON = event.clipboardData.getData("application/plectica");
    const clipboardHTML = event.clipboardData.getData("text/html");
    const clipboardText = event.clipboardData.getData("text/plain");
    // console.log('clipboardHTML',clipboardHTML,'clipboardText',clipboardText);
    // console.log('event.clipboardData.types',event.clipboardData.types)

    if (clipboardPlecticaJSON) {

      importJSON = clipboardPlecticaJSON;

    } else if (clipboardHTML && clipboardHTML.match(/data-base64-encoded-plectica-json/)) {
      /*
        if cards are copied from chrome and pasted into firefox or safari,
        we only get plain text and html data from the clipboard
        ...so lets grab the json we encoded in the html and decode it
      */
      const payload = html.match(/base64-encoded-plectica-json\=\"([^\"]+)\"/);
      if (payload && payload.length && payload[1]) {
        importJSON = decodeURIComponent(atob(payload[1]));
      }
    } else if (clipboardHTML) {

      pasteHTMLAsCards({ html: clipboardHTML, plaintext: clipboardText, canvas, position, event, parentId });

    } else if (clipboardText) {
      pasteTextAsCards({ text: clipboardText, canvas, position, event, parentId });
    }
  } else {
    /* when pasting from menus (no paste event from browser) */
    importJSON = localStorage.getItem(STORAGE_KEY);
  }

  let data;
  try {
    if (importJSON) {
      data = JSON.parse(importJSON);
      if (data && data.nodes) {
        for (const node of data.nodes) {
          if (node.canonicalNodeUUID) {
            delete node.canonicalNodeUUID;
          }
        }
      }

    }
  } catch (e) {
    console.log('error pasting',e);
  }
  if (!data) {
    return;
  }

  pasteData({ canvas, data, pasteWithinEnabled, inPlace: false, position });
}

function pasteData({ canvas, data, pasteWithinEnabled, forcedNewParent, forcePastePerspectives, inPlace, position }) {

  // if we're duplicating, make sure references are severed
  const nodes = data.nodes.map(n => Object.assign({},n));
  const links = data.links.map(l => Object.assign({},l));
  nodes.forEach(n => {
    n.absolutePosition = Object.assign({},n.absolutePosition);
    n.childrenPosition = Object.assign({},n.childrenPosition);
    if (n.attributes) {
      n.attributes = Object.assign({},n.attributes);
    }
    n.childrenIds = [];
  });
  const perspectives = JSON.parse(JSON.stringify((data.perspectives)));
  const regions = data.regions;
  const fromMapId = data.mapId;

  const pastedNodeIds = [];
  const pastedLinkIds = [];

  // apply new ids to pasted nodes & links
  const nodeOldtoNewIds = {};
  const linkOldtoNewIds = {};
  const nodeNewtoOldIds = {};
  const linkNewtoOldIds = {};
  let operationMessages = [];
  // generate new ids
  nodes.forEach(node => {
    const newUUID = nodeOldtoNewIds[node.id] = uuid();
    nodeNewtoOldIds[ newUUID ] = node.id;
  });

  links.forEach(link => {
    const newUUID = linkOldtoNewIds[link.id] = uuid();
    linkNewtoOldIds[ newUUID ] = link.id;
  });


  const cleanLinks = [];
  const cleanNodes = [];
  const droppedLinkNodes = {};

  for (let link of links) {
    link.id = linkOldtoNewIds[link.id];
    link.fromNodeId = nodeOldtoNewIds[link.fromNodeId];
    link.toNodeId = nodeOldtoNewIds[link.toNodeId];

    if (link.linkNodeId) {
      link.linkNodeId = nodeOldtoNewIds[link.linkNodeId];
    }
    // if a link was copied without both from & to nodes, then ignore the link
    if (!link.fromNodeId || !link.toNodeId) {
      if (link.linkNodeId) {
        droppedLinkNodes[link.linkNodeId] = 1;
      }
      continue;
    }

    cleanLinks.push(link);
  }

  if (perspectives && forcePastePerspectives) {
    for (const node of nodes) {
      const perspective = perspectives.find( p => p.pointNodeUUID === node.id);
      if (perspective) {

        if (perspective.members && perspective.members.nodes) {
          for (const pmId in perspective.members.nodes) {
            perspective.members.nodes[nodeOldtoNewIds[pmId]] = perspective.members.nodes[pmId];
            delete perspective.members.nodes[pmId];
          }
        }

        pui.addPerspectivePoint({
          label: perspective.label,
          color: pcui.findNextUnusedColor(),
          members: perspective.members,
          pointNodeUUID: nodeOldtoNewIds[node.id],
        });
        pui.refreshPerspectiveLists();
      }
    }
  }

  for (let node of nodes) {
    node.id = nodeOldtoNewIds[node.id];
    if (node.parentId) {
      // if a child is copied and not its parent, then this will be null and the card will become top level
      node.parentId = nodeOldtoNewIds[node.parentId];
    }

    // when cloning cards that were in a list or column, need to give them some positions
    if (!node.x && node.absolutePosition.x && !node.parentId) {
      node.x = node.absolutePosition.x;
    }
    if (!node.y && node.absolutePosition.y && !node.parentId) {
      node.y = node.absolutePosition.y;
    }
    cleanNodes.push(node);
  }

  // if cloning in place, don't alter position of cloned items
  if (!inPlace && !position) {
    normalizeNodePositions({
      nodes: nodes.filter(n => !n.parentId),
      regions
    });
  } else if (position) {
    setNodePositionsFromClick({nodes, regions, position});
  }

  if (inPlace) {
    normalizeEmancipatedClonePositions(nodes);
  }

  const { scale, tx, ty } = canvas.getMatrix();

  for (let node of cleanNodes) {

    if (!node.parentId && !inPlace && !position) {
      node.x = node.x - ((tx - getPasteOffsetX(canvas)) / scale);
      node.y = node.y - ((ty - getPasteOffsetY(canvas)) / scale);
    }
    node.createdById = window.user_id;
    if (node.attachedFile) {
      node.attachedFile.map_node_uuid = node.id;
    }
    if (node.childrenPosition && node.childrenPosition !== undefined) {
      node.cx = node.childrenPosition.x;
      node.cy = node.childrenPosition.y;
      node.uh = node.childrenPosition.userHeight;
      node.uw = node.childrenPosition.userWidth;
    }

    if (node.attributes) {
      for (const role in node.attributes) {
        const newAttributes = [];
        for (const attr of node.attributes[role]) {
          const newAttr = Object.assign({},attr);
          attr.id = uuid();
          newAttributes.push(attr);
        }
        node.attributes[role] = newAttributes;
      }
    }

    const nodeId = canvas.store.addNode(node);
    pastedNodeIds.push(nodeId);
  }


  for (let link of cleanLinks) {
    const linkId = canvas.store.addLink({
      id: link.id,
      fromNodeId: link.fromNodeId,
      toNodeId: link.toNodeId,
      linkNodeId: link.linkNodeId,
      angleStartToR: link.angleStartToR,
      angleEndToR: link.angleEndToR,
      color: link.color,
      lineType: link.lineType,
      lineWidth: link.lineWidth,
      lineCurvature: link.lineCurvature,
      direction: link.direction,
      createdById: window.user_id,
    });
    pastedLinkIds.push(linkId);
  }

  const pastedNodes = pastedNodeIds.map(nodeId => { return canvas.store.getNode(nodeId); });
  const pastedLinks = pastedLinkIds.map(linkId => { return canvas.store.getLink(linkId); });

  // flag nodes that were pasted in a collapsed state
  canvas.store.initializeHiddenStates(pastedNodes);

  // if a node is selected, paste inside that node
  const nodesToUpdate = pastedNodes.slice();
  if (forcedNewParent || (pasteWithinEnabled && canvas.getSelectedNodes().length == 1)) {
    normalizeNodePositions({nodes: pastedNodes});
    const newParent = forcedNewParent || canvas.getSelectedNodes()[0];
    nodesToUpdate.push(newParent);
    let i = 0;
    const _children = newParent.children();
    if (_children.length) {
      i = _children[ _children.length - 1 ].orderIndex + 1;
    }

    if (!_children.length && pastedNodes.filter(n => !n.parentId).length > 1) {
      const prevLayoutName = newParent.instance.getLayoutName();

      newParent.instance.setLayoutName('layout-freehand');
      operationMessages.push(
        canvas.operation.messages.setNodeLayoutName({
          nodeUUID: newParent.id,
          prevLayoutName,
          layoutName: 'layout-freehand',
        })
      );
    }

    for (const node of pastedNodes) {
      if (!node.parentId && !node.link()) {
        node.setOrderIndex(i++);
        node.adoptParent(newParent.id);
      }
    }

  }


  if (regions && !pasteWithinEnabled) {
    /* can't paste regions inside a card */
    applyRegionsToCanvas({ regions, canvas, position, inPlace, operationMessages });
  }


  if (pastedNodeIds.length || pastedLinkIds.length) {
    updatePastedBatchDisplay({
      nodes: nodesToUpdate,
      links: pastedLinks,
      canvas
    });

    const addBatchMessage = canvas.operation.messages.addBatch({
      nodes: pastedNodes,
      links: pastedLinks,
    });
    operationMessages.push(addBatchMessage);

    const pastedNodeTags = pastedNodeIds.map(id => canvas.nodeTags[id]).filter(n => Boolean(n));
    const pastedLinkTags = pastedLinkIds.map(id => canvas.linkTags[id]).filter(l => Boolean(l));

    if (perspectives && (fromMapId == canvas.store.getMapId() || forcePastePerspectives)) {
      // don't paste perspectives across maps for now...
      applyPerspectivesToPastedContent({
        canvas,
        fromMapId,
        operationMessages,
        perspectivesSnapshot: perspectives,
        pastedNodeTags,
        pastedLinkTags,
        pastedNodeIds,
        pastedLinkIds,
        nodeNewtoOldIds,
        linkNewtoOldIds,
        nodeOldtoNewIds,
      });
    }


    canvas.clearSelectedElements();
    canvas.setSelectedElements(pastedNodeTags,true);
    canvas.setSelectedElements(pastedLinkTags,true);
  }


  if (!inPlace) {
    if (Object.keys(nodeOldtoNewIds).length) {
      const topNode = canvas.store.getNode(Object.values(nodeOldtoNewIds)[0]).getTopLevelParent();
      const topNodeTag = canvas.nodeTags[topNode.id];

      const { prevNodePositions, nodePositions } = nui.fixOverlaps({ nodeTag: topNodeTag });
      if (nodePositions.length) {
        operationMessages.push(canvas.operation.messages.updateNodePositions({
          prevNodePositions,
          nodePositions,
          prevLinkAngles: [],
          linkAngles: [],
        }));
      }
    }

    canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: err => {
        console.warn(`paste batchOperation failed: ${err}`);
      }
    });

    // if we paste something multiple times, offset the location we paste it at so they're not all overlapping exactly
    _incrementPasteOffset();
  }
  return {
    nodeOldtoNewIds,
    linkOldtoNewIds,
    operationMessages
  };
}

function applyPerspectivesToPastedContent({ canvas, operationMessages, perspectivesSnapshot, pastedNodeTags, pastedLinkTags, pastedNodeIds, pastedLinkIds, nodeNewtoOldIds, linkNewtoOldIds, nodeOldtoNewIds }) {
  // add pasted items to the currently active perspective too
  if (pui.getActivePerspectiveCount()) {
    for (const activePerspective of pui.getActivePerspectives()) {
      if (!perspectivesSnapshot.find(p => p.id == activePerspective.id)) {
        perspectivesSnapshot = perspectivesSnapshot.push(activePerspective);
      }
    }
  }
  if (!(perspectivesSnapshot && perspectivesSnapshot.length)) return;
  for (const p of perspectivesSnapshot) {
    const pObj = canvas.store.getPerspective(p.id);
    if (pObj === undefined) {
      // don't paste deleted perspectives
      continue;
    }

    const originalPNodes = p.members.nodes;
    const originalPLinks = p.members.links;

    // add all pasted elements to active perspective
    if (pui.perspectiveIsActive(pObj.id)) {
      for (const id of pastedNodeIds) {
        pObj.addMemberNode(canvas.store.getNode(id));
      }
      for (const id of pastedLinkIds) {
        pObj.addMemberLink(canvas.store.getLink(id));
      }
    }

    let pNodeIds = pastedNodeIds.filter(id => originalPNodes[nodeNewtoOldIds[id]]);
    const pLinkIds = pastedLinkIds.filter(id => originalPLinks[linkNewtoOldIds[id]]);

    // if copying a point node, remove it from its original perspective
    if (pObj.pointNodeUUID && nodeOldtoNewIds[pObj.pointNodeUUID]) {
      const copiedPointNodeUUID = nodeOldtoNewIds[pObj.pointNodeUUID];
      pNodeIds = pNodeIds.filter(id => id != copiedPointNodeUUID);
    }

    const pastedPNodes = pNodeIds.map(id => canvas.nodeTags[id]);
    const pastedPLinks = pLinkIds.map(id => canvas.linkTags[id]);
    if (pastedNodeTags.length == 0 && pastedLinkTags.length == 0) {
      continue;
    }

    const nodeViews = {
      nodes: {}
    };

    // apply alternate titles and summaries from the original perspective snapshot to the pasted cards
    for (const id of pNodeIds) {
      nodeViews.nodes[ id ] = originalPNodes[ nodeNewtoOldIds[id] ];
    }

    pui.addBatchToPerspective({
      perspective: pObj,
      nodeTags: pastedPNodes,
      linkTags: pastedPLinks,
      nodeViews,
      operationMessages
    });

    // if perspective is active, light up the active cards / links
    if (pui.perspectiveIsActive(pObj.id)) {
      for (const nodeTag of pastedNodeTags) {
        if (pObj.members.nodes[nodeTag.id]) {
          nodeTag.perspectiveHighlight();
        }
      }
      for (const linkTag of pastedLinkTags) {
        if (pObj.members.links[linkTag.id]) {
          linkTag.perspectiveHighlight();
        }
      }
    }
  }
  // update sidebar
  if (canvas.perspectivesListingTag !== undefined) {
    canvas.perspectivesListingTag.update();
  }
  // update alt titles
  for (const nodeTag of pastedNodeTags) {
    nodeTag.updatePerspectiveDots();
  }
}

function applyRegionsToCanvas({ regions, canvas, position, inPlace, operationMessages }) {
  const newRegions = [];
  const { scale, tx, ty } = canvas.getMatrix();

  let xOffset = 0;
  let yOffset = 0;
  if (!inPlace && !position) {
    xOffset = ((tx - getPasteOffsetX(canvas)) / scale);
    yOffset = ((ty - getPasteOffsetY(canvas)) / scale);
  }

  for (const region of regions) {

    const newRegion = regionUI.createRegion({
      title: region.title,
      color: region.color,
      top: region.top - yOffset,
      left: region.left - xOffset,
      bottom: region.bottom - yOffset,
      right: region.right - xOffset,
      operationMessages
    });
    newRegions.push(newRegion);
  }
  canvas.regionsTag.update();
  regionUI.clearSelectedRegions();

  for (const region of newRegions) {
    regionUI.addRegionToSelection(region.id);
  }
}

function getPasteOffsetX(canvas) {
  if (canvas.toolsSidebarTag !== undefined && canvas.toolsSidebarTag.isOpen) {
    return pasteOffsetX + 320;
  }
  return pasteOffsetX;
}

function getPasteOffsetY(canvas) {
  return pasteOffsetY;
}


function normalizeNodePositions({nodes,regions,dx,dy}) {
  // reset positioning based on top-most/left-most item
  const filtered = nui.getHighestLevelNodesInList(nodes);

  if (regions && regions.length) {
    for (const region of regions) {
      filtered.push({
        x: region.left,
        y: region.top,
      });
    }
  }

  if (dx === null || dx === undefined) {
    dx = Math.min.apply(null,filtered.map(n => n.x));
  }
  if (dy === null || dy === undefined) {
    dy = Math.min.apply(null,filtered.map(n => n.y));
  }

  if (regions && regions.length) {
    for (const region of regions) {
      region.left = region.left - dx;
      region.right = region.right - dx;
      region.top = region.top - dy;
      region.bottom = region.bottom - dy;
    }
  }
  for (const node of filtered) {
    node.x = node.x - dx;
    node.y = node.y - dy;
  }
}

// when cloning former child nodes into top level nodes, set their x,y coords
function normalizeEmancipatedClonePositions(nodes) {
  for (const node of nodes) {
    if (!node.parentId) {
      node.x = node.absolutePosition.x;
      node.y = node.absolutePosition.y;
    }
  }
}

function setNodePositionsFromClick({nodes, regions, position}) {
  if ((!nodes || !nodes.length) && (!regions || !regions.length)) return;
  const { scale, tx, ty } = canvas.getMatrix();

  // find the top left of the selected nodes

  const filtered = nodes ? nui.getHighestLevelNodesInList(nodes) : [];
  if (regions && regions.length) {
    for (const region of regions) {
      filtered.push({
        x: region.left,
        y: region.top,
      });
    }
  }

  let left = filtered[0].x;
  let top = filtered[0].y;
  for (const node of filtered) {
    if (node.x < left) {
      left = node.x;
    }
    if (node.y < top) {
      top = node.y;
    }
  }
  const scaledX = ( position.x - tx ) / scale;
  const scaledY = ( position.y - ty ) / scale;

  const dx = left - scaledX;
  const dy = top - scaledY;
  if (regions && regions.length) {
    for (const region of regions) {
      region.left = region.left - dx;
      region.right = region.right - dx;
      region.top = region.top - dy;
      region.bottom = region.bottom - dy;
    }
  }
  //set other node positions relative to it
  for (const node of filtered) {
    node.x -= dx;
    node.y -= dy;
    if (node.absolutePosition) {
      node.absolutePosition.x -= dx;
      node.absolutePosition.y -= dy;
    }
  }
}

function getSelectionAsPlainText({canvas}) {
  const highestNodes = nui.getHighestLevelNodesInList( canvas.getSelectedNodes() );
  const text = getSystemsAsText({nodes: highestNodes, canvas, asHtml: false});
  return text;
}

// function getSystemsAsPlainText({nodes, level=0}) {
//   appUI.sortElementsByPosition({ elements: nodes });
//   const lines = [];
//   const nodeCount = nodes.length;
//   let currCount = 0;
//   for (const node of nodes) {
//     if (_nodeIsEmpty(node)) continue;

//     if (node.title) {
//       lines.push(`${node.title}`);
//     }
//     if (node.summary) {
//       lines.push(`${node.summary}`);
//     }

//     if (node.attributes && node.attributes.card && node.attributes.card.length) {
//       for (const attr of node.attributes.card) {
//         lines.push(`${attr.name}: ${attr.value}`);
//       }
//     }

//     if (node.summary || (node.attributes && node.attributes.card && node.attributes.card.length)) {
//       lines.push('');
//     }
//     currCount++;
//     if (node.children().length) {
//       const childText = getSystemsAsPlainText({
//         nodes: node.children(),
//         level: level+1,
//       });
//       if (childText) {
//         lines.push(childText);
//       }
//       if (level > 0 && currCount < nodeCount) {
//         /* separate separate top level cards */
//         lines.push('');
//       }
//     }

//     if (level === 0 && currCount < nodeCount) {
//       /* separate separate top level cards */
//       lines.push("\n");
//     }
//   }
//   return lines.join("\n");
// }

function getSelectionAsHTML({ canvas, encodedPayload }) {
  const highestNodes = nui.getHighestLevelNodesInList( canvas.getSelectedNodes() );
  const html = getSystemsAsText({nodes: highestNodes, canvas, encodedPayload, asHtml: true});
  return html;
}


function getSystemsAsText({nodes, canvas, level=0, encodedPayload, asHtml }) {

  const nonEmptyNodes = nodes.filter(n => !_nodeIsEmpty(n));
  if (nonEmptyNodes.length === 0) {
    return '';
  }
  /* if map has waypoints, sort node by waypoint index */
  appUI.sortElementsByWaypointsOrPosition({ elements: nonEmptyNodes });

  const lines = [];
  const nodeCount = nodes.length;
  let currCount = 1;
  let titleStyle="";
  let summaryStyle="";
  const longTitleLength = 80;

  const extraContentPresent = nonEmptyNodes.filter(n =>
    (n.summary && n.summary.trim()) ||
    (n.attributes && n.attributes.card && n.attributes.card.length) ||
    n.children().length
    ).length;
  const longLinesPresent = nonEmptyNodes.filter(n => n.title && n.title.length > longTitleLength).length;
  const isMultiLevel = nonEmptyNodes.filter(n => n.children().length).length;

  if (extraContentPresent) {
    titleStyle = 'font-weight: bold; font-family: sans-serif;';
    summaryStyle = 'font-weight: normal; font-family: sans-serif;';
  } else {
    titleStyle = 'font-weight: normal; font-family: sans-serif;';
    summaryStyle = 'font-weight: normal; font-family: sans-serif;';
  }
  if (encodedPayload && asHtml) {
    lines.push(`<span data-base64-encoded-plectica-json="${encodedPayload}"></span>`);
  }

  if (level > 0) {
    lines.push(_startList(asHtml));
  }
  let indent = '';
  for (let i = 1; i <= level; i++) {
    indent = `\t${indent}`;
  }
  for (const node of nonEmptyNodes) {
    if (_nodeIsEmpty(node)) continue;
    if (level > 0) {
      lines.push(_startListItem(asHtml));
    }
    if (node.title) {
      if (asHtml) {
        lines.push(`<span style="${titleStyle}">${node.title}</span>`);
      } else {
        const bullet = level > 0 ? '- ' : '';
        lines.push(`${indent}${bullet}${node.title}`);
      }
    }
    if (node.summary) {
      if (asHtml) {
        lines.push(`<br/><span style="${summaryStyle}">${node.summary}</span>`);
      } else {
        lines.push(`\n${indent}  ${node.summary}`);
      }
    }

    if (node.attributes && node.attributes.card && node.attributes.card.length) {
      lines.push(_startList(asHtml));
      for (const attr of node.attributes.card) {
        if (asHtml) {
          lines.push(`<li>
            <span style="font-weight: bold; font-family: sans-serif;">${attr.name}:</span>
            <span style="font-weight: normal; font-family: sans-serif;">${attr.value}</span>
            </li>
          `);
        } else {
          lines.push(`\n${indent}\t* ${attr.name}: ${attr.value}`);
        }
      }
      lines.push(_endList(asHtml));
    }

    if (asHtml && node.attachedFile && ALLOWED_IMAGE_FILE_TYPES.includes(node.attachedFile.file_type)) {
      lines.push(`<img src="${node.attachedFile.preview_url}">`);
    }


    if (node.summary || node.attachedFile || (node.attributes && node.attributes.card && node.attributes.card.length)) {
      lines.push('');
    }

    if (node.children().length) {
      const childText = getSystemsAsText({
        nodes: node.children(),
        level: level+1,
        asHtml
      });
      if (childText) {
        lines.push(childText);
      }
      if (level > 0 && currCount < nodeCount) {
        lines.push('');
      }
    }

    if (level > 0) {
      // if (longLinesPresent && currCount < nodeCount) {
      //   lines.push(_lineBreak(asHtml));
      // }
      lines.push(_endListItem(asHtml));
    }

    if (level === 0 && currCount < nodeCount) {
      // separate separate top level cards
      if (isMultiLevel) {
        // add extra newlines between top level cards if we have multi level systems
        // collapse successive newlines
        const lastLine = lines[lines.length - 1] || '';
        if (!lastLine.endsWith(_lineBreak(asHtml)) && (!asHtml || !lastLine.endsWith(_endList(asHtml)))) {
          lines.push(_lineBreak(asHtml));
        }
      }
      lines.push(_lineBreak(asHtml));
    }
    currCount++;
  }
  if (level > 0) {
    lines.push(_endList(asHtml));
    // if titles are long, extra break between lines
  }
  return lines.join('');
}


function _nodeIsEmpty(node) {
  if (!node.title
      && !node.summary
      && !(node.attributes && Object.values(node.attributes).length && node.attributes.card && node.attributes.card.length)
      && !node.children().length) return true;
  return false;
}

function _lineBreak(asHtml) {
  if (asHtml) {
    return '<br/>';
  }
  return "\n";
}

function _startList(asHtml) {
  if (asHtml) {
    return '<ul>';
  } else {
    return "\n";
  }
}

function _endList(asHtml) {
  if (asHtml) {
    return '</ul>';
  } else {
    return "";
  }
}

function _startListItem(asHtml) {
  if (asHtml) {
    return '<li>';
  }
  return '';
}

function _endListItem(asHtml) {
  if (asHtml) {
    return '</li>';
  } else {
    return "\n";
  }
}


function clipboardHasImage(event) {
  const isImage = event.clipboardData && Object.values(event.clipboardData.items || event.clipboardData.files).some((element) => element.type.indexOf("image") !== -1);
  return isImage;
}

/*
  create a new card and paste the file attachment in from clipboard
*/
function createCardAndAddAttachment({ canvas, event, position, parentId=null }) {
  let {x,y} = appUI.viewportCoordsToMapCoords(position);
  if (parentId) {
    parentNode = canvas.store.getNode(parentId);
    x = Math.max(0,parentNode.absolutePosition.x - x);
    y = Math.max(0,parentNode.absolutePosition.y - y);
  }
  const nodeId = canvas.store.addNode({
    title: '',
    x,
    y,
    createdById: window.user_id,
    orderIndex: 0,
    parentId,
  });
  canvas.operation.addNode({
    node: canvas.store.getNode(nodeId)
  });
  if (parentId) {
    canvas.nodeTags[parentId].updateLayers();
  } else {
    canvas.nodesTag.update();
  }
  // TODO: make this an atomic operation
  canvas.nodeTags[nodeId].handleFileUpload(event);
}


function pasteTextAsCards({ text, canvas, position, event, parentId=null }) {
  const lines = text.split(/\n\r|\n|\r/);
  const structure = getTextStructure(lines);
  const nodes = structureToCards({ structure, position, parentId, canvas });

  if (nodes.length) {
    const pastedNodeTags = nodes.map(n => canvas.getNodeTag({id: n.id}));
    canvas.operation.addBatch({
      nodes,
      links: [],
      onSuccess: () => {
        pastedNodeTags.forEach(nodeTag => nodeTag.setLinkIsHidden());
      },
    });

    if (pastedNodeTags.length) {
      canvas.setSelectedElements(pastedNodeTags);
    }
  }
}


function pasteHTMLAsCards({ html, plaintext, canvas, position, event, parentId=null }) {
  // console.log('pasting HTML');
  // console.log(html);

  const buffer = $('<div></div>').hide().append($(html));
  buffer.find('style').remove();
  buffer.find('head').remove();
  if (buffer.find('ul').length === 0 && buffer.find('ol').length === 0 && buffer.find('li').length === 0) {
    /*
      If our content doesn't have any list items, then just run with the plain text version
    */
    return pasteTextAsCards({
      text: plaintext,
      canvas,
      position,
      event,
      parentId
    });
  }


  const children = buffer.children();
  let parseData = parseHTML({ elements: children });
  //console.log('final parseData',parseData)
  const structure = convertParseToCardStructure({ parseData });
  //console.log('final structure',structure)
  const nodes = structureToCards({ structure, position, parentId, canvas });
  if (nodes.length) {
    const pastedNodeTags = nodes.map(n => canvas.getNodeTag({id: n.id}));
    if (pastedNodeTags.length) {
      canvas.setSelectedElements(pastedNodeTags);
    }
    canvas.operation.addBatch({
      nodes,
      links: [],
    });
  }
}

function parseHTML({ elements }) {
  const blacklist = {
    meta: 1,
    style: 1,
    title: 1,
  };
  let lines = [];
  const flatten = {
    span: 1,
    div: 1,
    b: 1,
    p: 1,
    br: 1,
  };
  const filteredElements = [];
  for (const element of elements) {
    const $element = $(element);

    const tagName = $element.prop('tagName').toLowerCase();
    if (blacklist[tagName]) continue;

    filteredElements.push(element);
  }

  //console.log("ELEMENTS",filteredElements);
  let indentLevel = 0;
  for (const element of filteredElements) {
    const $element = $(element);

    const tagName = $element.prop('tagName').toLowerCase();

    if (flatten[tagName] && filteredElements.length === 1) {
      return parseHTML({ elements: $element.children() });
    }

    if (tagName == 'br' ||
        (tagName == 'p' && !$element.text().trim()) ||
        (tagName == 'div' && !$element.text().trim())) {
      lines.push("--linebreak--");
      continue;
    }

    if (blacklist[tagName]) continue;

    if (flatten[tagName] && !$element.find('ul').length && !$element.find('li').length) {

      lines.push({tagName, text: $element.text(), indent: $element.css('margin-left')});

    } else if ($element.children().length) {

      if (tagName === 'li') {
//        console.log('MARGIN-LEFT',$element.css('margin-left'));
        lines.push({tagName, text: $element.text(), indent: $element.css('margin-left')});
        continue;
      }
      const parts = parseHTML({ elements: $element.children(), indent: $element.css('margin-left') });
      if (parts && parts.length) {

        if (flatten[tagName]) {
          lines = lines.concat(parts);
        } else {
          lines.push({tagName, parts});
        }
      }
    } else if ($element.text() && $element.text().trim().length) {
      lines.push({tagName, text: $element.text(), indent: $element.css('margin-left')});
    }
  }
  return lines;
}

function convertParseToCardStructure({ parseData }) {
  const topCards = [];
  let currentNode = {};
  let skip = {};
  for (const [i, item] of parseData.entries()) {
    if (skip[i]) continue;
    if (item === '--linebreak--') {
      continue;
    }

    if (item.text) {

      if (currentNode.title) {
        topCards.push(currentNode);
        currentNode = {};
      }

      currentNode.title = item.text;

      const nextItem = parseData[i+1];
      if (nextItem && nextItem.text && ['p', 'div', 'span'].includes(nextItem.tagName)) {
        currentNode.summary = nextItem.text;
        skip[i+1] = true;
      }

      continue;
    }

    if (item.parts && item.parts.length) {
      const parts = convertParseToCardStructure({ parseData: item.parts });
      if (!currentNode.parts) {
        currentNode.parts = [];
      }
      currentNode.parts = currentNode.parts.concat(parts);
    }
  }
  if (currentNode && Object.keys(currentNode).length) {
    topCards.push(currentNode);
  }

  return topCards;
}

function structureToCards({ structure, position, parentId=null, canvas }) {
  let {x,y} = appUI.viewportCoordsToMapCoords(position);
  if (parentId) {
    parentNode = canvas.store.getNode(parentId);
    x = Math.max(0,parentNode.absolutePosition.x - x);
    y = Math.max(0,parentNode.absolutePosition.y - y);
  }
  let nextX = x;
  let nextY = y;
  const bufferY = 30;
  const bufferX = 50;
  const cardsPerColumn = 10;
  let pastedNodes = [];
  let parentNode;
  let columns = 1;
  if (structure.length > cardsPerColumn) {
    columns = Math.ceil(structure.length / 10);
  }

  let rowCount = 0;
  let maxWidth = 0;

  let field = 'title';

  /*
    if any row of text is more than 255 characters
    then put all text into summaries instead of titles
  */

  for (const item of structure) {
    if (!item.title && !item.parts) continue;
    if (item.title) {
      if (item.title.length >= 255) {
        field = 'summary';
        break;
      }
    }
  }


  for (const [idx, item] of structure.entries()) {
    if (!item.title && !item.parts) continue;

    let title = '';
    let summary = '';

    if (field === 'title') {
      title = item.title;
    } else if (field === 'summary') {
      summary = item.title;
    }

    if (item.summary) {
      if (summary) {
        summary = summary + "\n\n" + item.summary;
      } else {
        summary = item.summary;
      }
    }
    const nodeId = canvas.store.addNode({
      title,
      summary,
      x: nextX,
      y: nextY,
      createdById: window.user_id,
      orderIndex: parentId ? idx : 0,
      parentId,
    });

    if (parentId) {
      canvas.nodeTags[parentId].updateLayers();
    } else {
      canvas.nodesTag.update();
    }

    const node = canvas.store.getNode(nodeId);
    const nodeTag = canvas.nodeTags[nodeId];
    pastedNodes.push(node);

    if (item.parts && item.parts.length) {
      const childNodes = structureToCards({ structure: item.parts, position, parentId: node.id, canvas});
      pastedNodes = pastedNodes.concat(childNodes);
    }

    nodeTag.discoverPosition();
    nextY = nextY + node.height + bufferY;
    maxWidth = Math.max(maxWidth,node.width);
    rowCount++;

    if (rowCount >= cardsPerColumn) {
      nextY = y;
      nextX = nextX + maxWidth + bufferX;
      rowCount = 0;
    }
  }

  return pastedNodes;
}


function getTextStructure(lines) {
  const structure = [];
  const cards = [];
  const lastCardAtIndentLevel = {};
  const indentLevels = [];

  // if a decent number of lines start with bullet chars then we have bullets
  const hasBulletChars = lines.filter(getBullet).length >= 0.1 * lines.length;
  const index = 0;
  const summaryIndexes = {};

  for (let [i, line] of lines.entries()) {
    if (summaryIndexes[i]) continue;
    if (!line || !line.trim()) continue;
    const card = {};
    const indentLevel = getIndentAmount(line);

    if (!indentLevels.includes(indentLevel)) {
      indentLevels.push(indentLevel);
      indentLevels.sort((a,b) => a - b);
    }
    let prevIndentLevel;
    if (indentLevels.length > 1) {
      const currentIndex = indentLevels.indexOf(indentLevel);
      prevIndentLevel = indentLevels[ currentIndex - 1];
    }

    const bullet = getBullet(line);
    if (bullet) {
      line = stripBullet(line);
    }
    card.title = line;
    if (hasBulletChars && lines[i+1]) {
      if (isSummaryLine(card.title, lines[i+1])) {
        card.summary = lines[i+1];
        summaryIndexes[i+1] = true;
      }
    }

    lastCardAtIndentLevel[indentLevel] = card;

    if ((prevIndentLevel !== undefined) && lastCardAtIndentLevel[prevIndentLevel]) {
      const parent = lastCardAtIndentLevel[prevIndentLevel];
      if (!parent.parts) {
        parent.parts = [];
      }
      parent.parts.push(card);
    } else {
      structure.push(card);
    }
  }

  return structure;
}

function isSummaryLine(title, summary) {
  // guess that we're a summary if we're indented ~two spaces beyond the title
  // and if we have no bullet
  if (getBullet(summary)) return false;
  if (getIndentAmount(title) >= getIndentAmount(summary)) return false;
  if (summary.match(/^\s*  /)) return true;
}

function getIndentAmount(line) {

  // if we start with tabs then only tabs count
  if (line.startsWith("\t")) {
    const indentLevel = line.match(/^(\t+)/)[1].length;
    return indentLevel;
  }

  // otherwise count up whitespace chars
  const indentSpaces = line.match(/^(\s+)/);
  if (indentSpaces && indentSpaces.length > 0) {
    return indentSpaces[1].length;
  }

  return 0;
}

/*
  return true if the string starts with a bullet character
*/
function getBullet(line) {
  const match = line.match(/^[\s\t]*([\*\-\u2022\u2023\u25E6\u2043\u2219\d+]\.?\s[A-z])/);
  if (match && match.length > 0) {
    return match[1];
  }
}

function stripBullet(line) {
  return line.replace(/^[\s\t]*[\*\-\u2022\u2023\u25E6\u2043\u2219\d+]\.?\s/,'');
}


export default {
  cloneSelectionInPlace,
  pasteContentExists,
  copy,
  paste,
  normalizeNodePositions,
  copyData,
  pasteData,
  getSelectionAsPlainText,
  getSystemsAsText,
};



// WEBPACK FOOTER //
// ./starling/src/lib/copy-paste.js